import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { SEO, buildBreadcrumbSchema } from "@/components/seo/SEO";
import { Shield, Lock, Eye, FileText, Server, Users, Mail } from "lucide-react";

const Privacy = () => {
  return (
    <div className="min-h-screen app-surface">
      <SEO
        title="Privacy & GDPR — Email2Contact"
        description="Email2Contact privacy policy: 100% EU-hosted AI processing (Azure West Europe), zero data retention, GDPR-compliant, no AI training on your data."
        path="/privacy"
        jsonLd={buildBreadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Privacy", path: "/privacy" },
        ])}
      />
      <Navbar />
      
      <main className="py-16 md:py-24">
        <div className="container px-4 max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex items-center gap-2 app-pill app-pill-primary mb-6">
              <Shield className="w-3.5 h-3.5" />
              <span>EU Privacy Standards</span>
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mb-3">
              Privacy Policy
            </h1>
            <p className="text-muted-foreground">
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>

          {/* Key Highlights */}
          <Card className="app-tile p-8 mb-12">
            <h2 className="font-display text-2xl font-semibold tracking-tight mb-6 flex items-center gap-2">
              <Lock className="w-6 h-6 text-primary" />
              Privacy at a Glance
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <Server className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Secure EU Processing</h3>
                  <p className="text-sm text-muted-foreground">
                    Email signatures processed via ConvertAPI (EU) and Azure OpenAI (West Europe). Zero permanent storage—ephemeral only.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <Shield className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Privacy-First AI</h3>
                  <p className="text-sm text-muted-foreground">
                    Microsoft DPA guarantees no training, no reuse. All processing in EU data centers.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <Users className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">You Control Your Data</h3>
                  <p className="text-sm text-muted-foreground">
                    Review and edit before saving anything
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <Eye className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Built for EU Privacy</h3>
                  <p className="text-sm text-muted-foreground">
                    Designed with Privacy by Design principles
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Detailed Privacy Policy */}
          <div className="prose prose-slate max-w-none space-y-8">
            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4 flex items-center gap-2">
                <FileText className="w-6 h-6 text-primary" />
                1. Introduction
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Email2Contact ("we," "our," or "the Add-in") is an Outlook add-in developed by Celsios GmbH, 
                registered in Germany. We are committed to protecting your privacy and ensuring the security of 
                your personal data in accordance with the General Data Protection Regulation (GDPR) and other 
                applicable data protection laws.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                This Privacy Policy explains how Email2Contact processes data when you use our Outlook add-in.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">2. Data Controller</h2>
              <Card className="app-tile p-6 bg-muted/30">
                <p className="font-semibold mb-2">Celsios GmbH</p>
                <p className="text-sm text-muted-foreground">
                  Address: Cosimastrasse 121, 81925 Munich, Germany<br />
                  Email: support@email2contact.com<br />
                  Website: https://email2contact.com
                </p>
              </Card>
            </section>

            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">3. What Data We Process</h2>
              
              <h3 className="text-xl font-semibold mb-3">3.1 Email Content Processing (EU-Based AI)</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                When you click "AI-Enhance," the following data is temporarily transmitted to secure EU servers:
              </p>
              
              <div className="mb-4">
                <p className="font-semibold mb-2">What is sent:</p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li><strong>Email signature HTML</strong>: Converted to PNG image via ConvertAPI EU</li>
                  <li><strong>Email body text</strong>: For context</li>
                  <li><strong>Sender email address</strong>: To identify the contact</li>
                </ul>
              </div>

              <div className="mb-4">
                <p className="font-semibold mb-2">Where it is processed:</p>
                <ol className="list-decimal pl-6 text-muted-foreground space-y-3">
                  <li>
                    <strong>ConvertAPI (EU Region):</strong> Converts email HTML to PNG image
                    <ul className="list-disc pl-6 mt-1 text-sm">
                      <li>Location: EU data centers</li>
                      <li>Processing: Ephemeral (no storage)</li>
                      <li>Privacy: <a href="https://www.convertapi.com/privacy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">https://www.convertapi.com/privacy</a></li>
                    </ul>
                  </li>
                  <li>
                    <strong>Azure OpenAI Vision (West Europe, Netherlands):</strong> Analyzes the PNG to extract contact fields
                    <ul className="list-disc pl-6 mt-1 text-sm">
                      <li>Location: West Europe (Azure Data Zone Standard)</li>
                      <li>Processing: In-memory only, zero retention</li>
                      <li>DPA: Microsoft Data Processing Agreement (no training, no secondary use)</li>
                    </ul>
                  </li>
                </ol>
              </div>

              <Card className="app-tile p-4 border-emerald-500/30 bg-emerald-500/5">
                <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400 mb-2">✓ Critical Privacy Protections:</p>
                <ul className="text-sm text-emerald-700/90 dark:text-emerald-400/90 space-y-1">
                  <li>✓ All processing occurs exclusively in EU data centers</li>
                  <li>✓ Zero permanent storage—data is deleted immediately after processing</li>
                  <li>✓ TLS 1.3 encryption for all transmissions</li>
                  <li>✓ No logging of personal data (technical status codes only)</li>
                  <li>✓ You must explicitly click "AI-Enhance" to trigger this processing</li>
                </ul>
              </Card>

              <p className="text-sm text-muted-foreground mt-4">
                <strong>Legal Basis:</strong> Consent (Art. 6 Para. 1 lit. a GDPR) via explicit user action
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">3.2 Extracted Contact Information</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                The add-in extracts the following types of contact information from email signatures:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>First and last names</li>
                <li>Company names</li>
                <li>Job titles</li>
                <li>Email addresses</li>
                <li>Phone numbers (mobile and business)</li>
                <li>Website URLs</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed">
                This information is temporarily stored in your browser's memory while you review and edit it. 
                Once you click "Save," it is sent directly to your Microsoft Outlook/Exchange account via the 
                Microsoft Graph API. We do not store, transmit, or have access to this data at any point.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">3.3 Usage Data</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We collect minimal anonymized usage statistics to improve the add-in:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Number of times the add-in is activated</li>
                <li>Error logs (without personal data)</li>
                <li>Browser and Outlook version information</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-4">
                This data is aggregated and anonymized and cannot be linked to individual users.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">3.4 Optional Smart Features (Explicit Opt-In Required)</h3>
              
              <div className="space-y-4">
                <Card className="app-tile p-4">
                  <h4 className="font-semibold mb-2">Smart Categories (Azure OpenAI GPT-4o, West Europe)</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li><strong>Purpose:</strong> Automatically classify companies into custom categories</li>
                    <li><strong>Data sent:</strong> Company name, website, job title</li>
                    <li><strong>Processing:</strong> Azure OpenAI GPT-4o (West Europe, zero retention)</li>
                    <li><strong>Consent:</strong> Enabled via Settings → Smart Categories</li>
                    <li><strong>Legal Basis:</strong> Consent (Art. 6 Para. 1 lit. a GDPR)</li>
                  </ul>
                </Card>

                <Card className="app-tile p-4">
                  <h4 className="font-semibold mb-2">Smart Notes (Lovable AI / Google Gemini 2.5 Flash)</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li><strong>Purpose:</strong> Generate research notes about companies (web-crawling capability)</li>
                    <li><strong>Data sent:</strong> Company name, website, custom research prompt</li>
                    <li><strong>Processing:</strong> Google Gemini API (EU region preferred, ephemeral)</li>
                    <li><strong>Consent:</strong> Enabled via Settings → Smart Notes</li>
                    <li><strong>Legal Basis:</strong> Consent (Art. 6 Para. 1 lit. a GDPR)</li>
                  </ul>
                </Card>
              </div>

              <Card className="app-tile p-4 mt-4 border-emerald-500/30 bg-emerald-500/5">
                <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400 mb-2">✓ Critical Privacy Protection:</p>
                <ul className="text-sm text-emerald-700/90 dark:text-emerald-400/90 space-y-1">
                  <li>✓ Both features require explicit opt-in via Settings</li>
                  <li>✓ No personal email content is sent—only company metadata</li>
                  <li>✓ Zero data retention by AI providers</li>
                  <li>✓ You can disable these features at any time</li>
                </ul>
              </Card>
            </section>

            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">4. Legal Basis for Processing</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Under GDPR Article 6, we process personal data based on the following legal grounds:
              </p>
              
              <div className="space-y-4">
                <Card className="app-tile p-4">
                  <h3 className="font-semibold mb-2">Consent (Art. 6 Para. 1 lit. a GDPR)</h3>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• AI-powered contact extraction (triggered by "AI-Enhance" button)</li>
                    <li>• Smart Categories (enabled via Settings)</li>
                    <li>• Smart Notes (enabled via Settings)</li>
                  </ul>
                </Card>

                <Card className="app-tile p-4">
                  <h3 className="font-semibold mb-2">Legitimate Interest (Art. 6 Para. 1 lit. f GDPR)</h3>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Error logging (anonymized, technical only)</li>
                    <li>• Performance monitoring (aggregated, no personal data)</li>
                  </ul>
                </Card>

                <Card className="app-tile p-4">
                  <h3 className="font-semibold mb-2">Contract Performance (Art. 6 Para. 1 lit. b GDPR)</h3>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Providing core add-in functionality</li>
                    <li>• Saving contacts to your Microsoft Outlook account</li>
                  </ul>
                </Card>
              </div>
            </section>

            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">5. Data Recipients and Third-Party Services</h2>
              
              <h3 className="text-xl font-semibold mb-3">5.1 Microsoft Services</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Email2Contact integrates with Microsoft services:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li><strong>Microsoft Graph API</strong>: Used to save contacts to your Outlook account. Data is transmitted 
                directly from your browser to Microsoft's servers using your authenticated session.</li>
                <li><strong>Microsoft Office.js</strong>: The official Microsoft library for Outlook add-ins, used to 
                access email content locally within Outlook.</li>
              </ul>
              <p className="text-sm text-muted-foreground">
                Microsoft processes this data according to their{" "}
                <a href="https://privacy.microsoft.com" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                  Privacy Statement
                </a>.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">5.2 AI Processing Services (EU-Based, Ephemeral Only)</h3>
              
              <div className="space-y-4 mb-6">
                <Card className="app-tile p-4">
                  <h4 className="font-semibold mb-2">ConvertAPI (EU Region)</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li><strong>Purpose:</strong> HTML-to-image conversion for AI analysis</li>
                    <li><strong>Data sent:</strong> Email signature HTML (temporary)</li>
                    <li><strong>Retention:</strong> Zero (ephemeral processing)</li>
                    <li><strong>Location:</strong> EU data centers</li>
                    <li><strong>Privacy Policy:</strong> <a href="https://www.convertapi.com/privacy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">https://www.convertapi.com/privacy</a></li>
                  </ul>
                </Card>

                <Card className="app-tile p-4">
                  <h4 className="font-semibold mb-2">Azure OpenAI Service (West Europe, Netherlands)</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li><strong>Purpose:</strong> AI-powered contact extraction & company classification</li>
                    <li><strong>Data sent:</strong> Email signature image (PNG), company metadata</li>
                    <li><strong>Retention:</strong> Zero (in-memory processing only)</li>
                    <li><strong>Location:</strong> West Europe (Azure Data Zone Standard)</li>
                    <li><strong>DPA:</strong> Microsoft Data Processing Agreement (no training, no reuse)</li>
                    <li><strong>Privacy Policy:</strong> <a href="https://privacy.microsoft.com" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">https://privacy.microsoft.com</a></li>
                  </ul>
                </Card>

                <Card className="app-tile p-4">
                  <h4 className="font-semibold mb-2">Lovable AI / Google Gemini (Smart Notes Only)</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li><strong>Purpose:</strong> Company research note generation (optional feature)</li>
                    <li><strong>Data sent:</strong> Company name, website, custom research prompt</li>
                    <li><strong>Retention:</strong> Zero (ephemeral)</li>
                    <li><strong>Location:</strong> Google Cloud (EU region preferred)</li>
                    <li><strong>Privacy Policy:</strong> <a href="https://policies.google.com/privacy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy</a></li>
                  </ul>
                </Card>
              </div>

              <h3 className="text-xl font-semibold mb-3">5.3 No Other Third Parties</h3>
              <p className="text-muted-foreground leading-relaxed">
                We do not share your data with advertising networks, analytics trackers, or data brokers.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">6. Data Storage and Retention</h2>
              
              <h3 className="text-xl font-semibold mb-3">6.1 No Server-Side Storage</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                <strong>Email2Contact does not store any contact data or email content on our servers.</strong> All 
                processing happens locally, and extracted information is saved directly to your Microsoft account.
              </p>

              <h3 className="text-xl font-semibold mb-3">6.2 Local Browser Storage</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                The add-in may temporarily use your browser's local storage to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Cache your authentication session with Microsoft</li>
                <li>Store user preferences (e.g., interface settings)</li>
                <li>Remember that you've seen the privacy notice</li>
              </ul>
              <p className="text-sm text-muted-foreground">
                This data remains on your device and can be cleared at any time through your browser settings.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">6.3 Retention Period</h3>
              <p className="text-muted-foreground leading-relaxed">
                Since we don't store personal data on our servers, there is no retention period. Data processed 
                by the add-in exists only during your active session and is discarded when you close the add-in 
                or your browser.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">7. Your Rights Under GDPR</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                As a data subject, you have the following rights:
              </p>

              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">Right of Access (Art. 15 GDPR)</h3>
                  <p className="text-sm text-muted-foreground">
                    Request information about what personal data we process about you. Since Email2Contact 
                    doesn't store your data, we will confirm that we hold no data about you.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Right to Rectification (Art. 16 GDPR)</h3>
                  <p className="text-sm text-muted-foreground">
                    Correct any inaccurate data. Since all data is in your Microsoft account, you can edit 
                    contacts directly in Outlook.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Right to Erasure (Art. 17 GDPR)</h3>
                  <p className="text-sm text-muted-foreground">
                    Request deletion of your data. To remove any data, simply uninstall the add-in and clear 
                    your browser's local storage.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Right to Restrict Processing (Art. 18 GDPR)</h3>
                  <p className="text-sm text-muted-foreground">
                    You can stop using the add-in at any time to cease all data processing.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Right to Data Portability (Art. 20 GDPR)</h3>
                  <p className="text-sm text-muted-foreground">
                    Your contacts are stored in your Microsoft account and can be exported using Outlook's 
                    built-in export features.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Right to Object (Art. 21 GDPR)</h3>
                  <p className="text-sm text-muted-foreground">
                    You can object to processing at any time by uninstalling the add-in.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Right to Lodge a Complaint (Art. 77 GDPR)</h3>
                  <p className="text-sm text-muted-foreground">
                    You have the right to file a complaint with your local data protection authority if you 
                    believe we've violated your privacy rights.
                  </p>
                </div>
              </div>

              <Card className="p-4 mt-6 bg-muted/50">
                <p className="font-semibold mb-2">To Exercise Your Rights:</p>
                <p className="text-sm text-muted-foreground">
                  Contact us at{" "}
                  <a href="mailto:support@email2contact.com" className="text-primary hover:underline">
                    support@email2contact.com
                  </a>{" "}
                  or use the contact form on our{" "}
                  <a href="/support" className="text-primary hover:underline">support page</a>.
                </p>
              </Card>
            </section>

            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">8. Data Security</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We implement appropriate technical and organizational measures to protect your data:
              </p>
              
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3">Technical Measures:</h3>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li><strong>TLS 1.3 encryption</strong> for all data transmissions</li>
                  <li><strong>Zero permanent storage</strong> (ephemeral processing only)</li>
                  <li><strong>EU-region processing</strong> (Azure West Europe, ConvertAPI EU)</li>
                  <li><strong>OAuth 2.0 authentication</strong> with Microsoft</li>
                  <li><strong>No data logging</strong> (technical status codes only)</li>
                </ul>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3">Organizational Measures:</h3>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li><strong>Microsoft Azure DPA</strong> (no training, no model reuse)</li>
                  <li><strong>ConvertAPI DPA</strong> (ephemeral processing, no storage)</li>
                  <li><strong>Regular security audits</strong></li>
                  <li><strong>Quarterly verification</strong> of Azure deployment region</li>
                </ul>
              </div>

              <Card className="app-tile p-4 border-emerald-500/30 bg-emerald-500/5">
                <h3 className="text-lg font-semibold text-emerald-700 dark:text-emerald-400 mb-3">AI Processing Guarantees:</h3>
                <ul className="text-sm text-emerald-700/90 dark:text-emerald-400/90 space-y-1">
                  <li>✓ Azure OpenAI: West Europe (Data Zone Standard)</li>
                  <li>✓ ConvertAPI: EU region only</li>
                  <li>✓ Lovable AI: Ephemeral processing (Google Gemini)</li>
                  <li>✓ All AI providers: Zero data retention after response</li>
                </ul>
              </Card>
            </section>

            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4 flex items-center gap-2">
                <Mail className="w-6 h-6 text-primary" />
                9. Waitlist & Product Updates
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                If you sign up for our waitlist or newsletter, we collect and process your email address 
                and optionally your name and company information.
              </p>
              
              <h3 className="text-xl font-semibold mb-3">9.1 What We Collect</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li><strong>Email address</strong> (required): To send you launch notifications and product updates</li>
                <li><strong>Name</strong> (optional): To personalize communications</li>
                <li><strong>Company</strong> (optional): To understand our user base</li>
                <li><strong>Interest reason</strong> (optional): To improve our product based on your needs</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">9.2 How We Use This Data</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We use your waitlist data to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Notify you when Email2Contact launches</li>
                <li>Send you exclusive early access invitations</li>
                <li>Provide product updates and feature announcements</li>
                <li>Gather feedback to improve the product</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">9.3 Data Storage</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Waitlist data is stored securely on EU-based servers (Supabase EU region: eu-central-1) 
                in compliance with GDPR requirements.
              </p>

              <h3 className="text-xl font-semibold mb-3">9.4 Your Rights</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You can unsubscribe at any time by:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Clicking the unsubscribe link in any email we send</li>
                <li>Contacting us at <a href="mailto:support@email2contact.com" className="text-primary hover:underline">support@email2contact.com</a></li>
                <li>Requesting full deletion of your data</li>
              </ul>

              <Card className="app-tile p-4 border-emerald-500/30 bg-emerald-500/5">
                <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400 mb-2">✓ Your Data, Your Control:</p>
                <p className="text-sm text-emerald-700/90 dark:text-emerald-400/90">
                  We will never share your waitlist data with third parties for marketing purposes. 
                  Your information is used solely to keep you informed about Email2Contact.
                </p>
              </Card>
            </section>

            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">9. International Data Transfers</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Email2Contact is hosted within the European Union (Germany). Since we don't store personal data 
                on our servers, there are no international data transfers from Email2Contact.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                However, when you save contacts to your Microsoft account, Microsoft may process data in 
                accordance with their global infrastructure. Microsoft complies with GDPR requirements for 
                international transfers.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">10. Children's Privacy</h2>
              <p className="text-muted-foreground leading-relaxed">
                Email2Contact is not intended for use by individuals under the age of 16. We do not knowingly 
                collect personal data from children. If you believe a child has provided data through our 
                add-in, please contact us immediately.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">11. Changes to This Privacy Policy</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We may update this Privacy Policy from time to time to reflect changes in our practices or 
                legal requirements. We will notify you of any material changes by:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Updating the "Last updated" date at the top of this page</li>
                <li>Displaying a notice in the add-in interface</li>
                <li>Sending an email notification (if applicable)</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed">
                Continued use of Email2Contact after changes indicates acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4 flex items-center gap-2">
                <Mail className="w-6 h-6 text-primary" />
                12. Contact Information
              </h2>
              <Card className="p-6">
                <p className="font-semibold mb-4">For privacy-related questions or concerns:</p>
                <div className="space-y-2 text-sm">
                  <p><strong>Data Protection Officer:</strong> support@email2contact.com</p>
                  <p><strong>General Support:</strong> support@email2contact.com</p>
                  <p><strong>Company:</strong> Celsios GmbH</p>
                  <p><strong>Address:</strong> Cosimastrasse 121, 81925 Munich, Germany</p>
                  <p><strong>Website:</strong> <a href="https://email2contact.com" className="text-primary hover:underline">https://email2contact.com</a></p>
                </div>
              </Card>
            </section>

            <section className="border-t pt-8">
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">Summary: Your Privacy in Practice</h2>
              <Card className="p-6 bg-green-50 border-green-200">
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span><strong>EU-Based AI Processing:</strong> All AI processing in EU data centers (West Europe) with zero retention</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span><strong>Ephemeral Processing:</strong> No permanent storage—data deleted immediately after analysis</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span><strong>Microsoft DPA Guarantees:</strong> No training, no model reuse, no secondary data use</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span><strong>Full Control:</strong> You review and approve everything before saving</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span><strong>GDPR Compliant:</strong> Built with Privacy by Design from the ground up</span>
                  </li>
                </ul>
              </Card>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Privacy;
